import React, {ReactElement, useRef, useState} from 'react';
import {Box, Paper, Stack, Table, TableCell, TableHead, TableRow, TableSortLabel} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import {SelectChangeEvent} from '@mui/material/Select';
import {ExplorerTreeView} from './ExplorerTreeView.component';
import {ResourceType} from '../../../@types/resource-type';
import {uuid} from '../../../utils/utils';
import SafetyService from '../../../services/safety.service';
import EventSequenceSummary from '../../../@types/event-sequence-summary';
import {addOrUpdateSafetyModel, makeSafetyModel} from '../../../utils/safety-model-utils';
import SafetyModel from '../../../@types/safety-model';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {toast} from 'react-toastify';
import {BasicPage, defaultPageLarge} from '../../../@types/spring-data-page';
import authService from '../../../services/auth.service';
import {OccurrenceReportTable} from './components/OccurrenceReportTable.component';
import {NewOccurrenceReportTable} from './components/NewOccurrenceReportTable.component';
import {Moment} from 'moment';
import {OccurrenceReportDescriptorTable} from './components/OccurrenceReportDescriptorTable.component';

// Import extracted types and utilities
import {DESCRIPTOR_TYPES, ExplorerProps, Order, PAGE_SIZES} from './explorer-types';
import {EXPLORER_TYPE_CONFIG, ExplorerTypeConfig} from './explorer-config';
import {
    convertToTreeViewItems, createDefaultPage, resourcesToSelectListItems, updateSafetyModelChildren
} from './explorer-utils';
import {ExplorerHeader} from './ExplorerHeader.component';
import {ExplorerFilters} from './ExplorerFilters.component';
import {ExplorerPagination} from './ExplorerPagination.component';

export default function Explorer(props: ExplorerProps): ReactElement {

    const {exploreMode, objects, additionalResources, onClickNew, onClickItem, loadObjects, setObjects} = props;

    const config: ExplorerTypeConfig = EXPLORER_TYPE_CONFIG[props.type] || EXPLORER_TYPE_CONFIG.default;
    const [objectsLoading, setObjectsLoading] = React.useState<boolean>(false);
    const [searchText, setSearchText] = React.useState<string>('');
    const [selectedResource, setSelectedResource] = React.useState<number>(0);
    const [page, setPage] = useState<BasicPage>(defaultPageLarge);
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<string>('group');
    const [newOccurrenceStartSearchDate, setNewOccurrenceStartSearchDate] = React.useState<Moment | null>(null);
    const [newOccurrenceEndSearchDate, setNewOccurrenceEndSearchDate] = React.useState<Moment | null>(null);
    const [selectedDescriptorType, setSelectedDescriptorType] = useState<number>(0);
    const {setTargetGroup, addOrUpdateAdditionalResource} = useWorkspaceContext();
    const uuidRef = useRef(uuid());

    // Helper to consolidate loadObjects calls
    const triggerLoadObjects = (overrides: Partial<{
        page: BasicPage, resource: number, search: string
    }> = {}): void => {
        const loadPage = overrides.page || createDefaultPage(orderBy, order);
        setPage(loadPage);
        loadObjects(setObjectsLoading, loadPage, overrides.resource !== undefined ? overrides.resource : selectedResource, overrides.search !== undefined ? overrides.search : searchText, newOccurrenceStartSearchDate, newOccurrenceEndSearchDate, selectedDescriptorType);
        setExpanded([]);
    };

    /**
     * Effect to initialize the orderBy and order state based on the current objects and config.
     */
    React.useEffect(() => {
        if (objects === null || !objects.resources) {
            const expectedOrderBy = config.defaultOrderBy;

            if (orderBy === 'uniqueId' && expectedOrderBy !== 'uniqueId') {
                setOrderBy(expectedOrderBy);
            } else if (orderBy === 'name' && expectedOrderBy === 'uniqueId') {
                setOrderBy(expectedOrderBy);
            } else if (orderBy !== 'name' && orderBy !== 'group' && expectedOrderBy !== 'uniqueId') {
                setOrderBy('group');
                setOrder('asc');
            } else {
                triggerLoadObjects();
            }
        }
    }, [objects]);

    /**
     * Effect to load objects when the order or orderBy changes
     */
    React.useEffect(() => {
        triggerLoadObjects();
    }, [order, orderBy]);

    /**
     * Effect to load objects when the selected resource changes
     */
    React.useEffect(() => {
        triggerLoadObjects();
    }, [selectedDescriptorType]);

    /**
     * Effect to load objects when the selected resource changes
     */
    React.useEffect(() => {
        triggerLoadObjects();
    }, [newOccurrenceStartSearchDate, newOccurrenceEndSearchDate]);

    function onSelectChange(event: React.ChangeEvent<{ value: unknown }>): void {
        const id = event.target.value as number;
        setSelectedResource(id);
        setTargetGroup(id);
        const lgpage = props.type !== ResourceType.SAFETY_MODELS ? defaultPageLarge : createDefaultPage(orderBy, order);
        triggerLoadObjects({page: lgpage, resource: id});
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setSearchText(event.target.value);
        triggerLoadObjects({search: event.target.value});
    }

    function onPageSizeChange(event: SelectChangeEvent<number>): void {
        let newPage: BasicPage = {...page, size: event.target.value as number, offset: 0};
        triggerLoadObjects({page: newPage});
    }

    const onPageChange = (event: React.ChangeEvent<unknown>, pageNumber: number): void => {
        let newPage = {...page, offset: pageNumber - 1};
        triggerLoadObjects({page: newPage});
    }

    const handleRequestSort = (property: string,): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const onDescriptorTypeChange = (event: SelectChangeEvent<number>): void => {
        setSelectedDescriptorType(event.target.value as number);
    };

    const handleTreeViewSelection = (_event: React.SyntheticEvent, nodeId: string | null): void => {
        if (!nodeId) return;

        const [resourceType, resourceId] = nodeId.split('-');
        if (resourceType !== ResourceType.SAFETY_MODELS) return;

        const selectedSafetyModel: SafetyModel = objects?.resources?.find((resource: SafetyModel) => resource.id === parseInt(resourceId));

        if (!selectedSafetyModel || selectedSafetyModel.numberEventSequences === 0 || selectedSafetyModel.children) {
            return;
        }

        setObjectsLoading(true);
        SafetyService.getEventSequences(parseInt(resourceId))
            .then((eventSequences: EventSequenceSummary[]) => {
                const updatedSafetyModel = makeSafetyModel(selectedSafetyModel);

                eventSequences.forEach(es => es.role = updatedSafetyModel.group.role());
                updatedSafetyModel.children = eventSequences;

                setObjects({
                    objectType: ResourceType.SAFETY_MODELS,
                    resources: addOrUpdateSafetyModel(updateSafetyModelChildren(updatedSafetyModel), objects?.resources as SafetyModel[]),
                    page: objects?.page
                });

                addOrUpdateAdditionalResource({
                    objectType: ResourceType.SAFETY_MODELS,
                    resources: addOrUpdateSafetyModel(updatedSafetyModel, objects?.resources as SafetyModel[])
                });

                setExpanded([...expanded, nodeId]);
            })
            .catch((error) => {
                toast.error('Error loading event sequences for safety model ' + resourceId + ': ' + error.message);
            })
            .finally(() => {
                setObjectsLoading(false);
            });
    };

    return (<Box sx={{
        display: 'flex',
        overflowY: 'auto',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        backgroundColor: '#FAFEFE'
    }}>
        <Stack sx={{width: '100%', overflowX: 'hidden', p: 0.8}}>
            <ExplorerHeader
                label={props.label}
                exploreMode={exploreMode}
                showNewButton={config.showNewButton !== false}
                newButtonDisabled={props.type === ResourceType.GROUPS && !authService.isSuperUser()}
                onClickNew={onClickNew}
            />
            <ExplorerFilters
                config={config}
                selectedResource={selectedResource}
                searchText={searchText}
                newOccurrenceStartSearchDate={newOccurrenceStartSearchDate}
                newOccurrenceEndSearchDate={newOccurrenceEndSearchDate}
                selectedDescriptorType={selectedDescriptorType}
                additionalResourceItems={resourcesToSelectListItems(additionalResources?.resources)}
                descriptorTypes={DESCRIPTOR_TYPES.map(type => ({id: type.id, name: type.name}))}
                onResourceChange={onSelectChange}
                onSearchTextChange={onTextChange}
                onStartDateChange={setNewOccurrenceStartSearchDate}
                onEndDateChange={setNewOccurrenceEndSearchDate}
                onDescriptorTypeChange={onDescriptorTypeChange}
            />
            <React.Fragment>

                {objectsLoading && <Box sx={{width: '100%'}}>
                    <LinearProgress aria-label={objectsLoading ? 'progress bar' : undefined}/>
                </Box>}

                {props.type !== ResourceType.GROUPS && config.showTable && <Paper
                    sx={{mt: '20px', width: '100%', backgroundColor: 'rgb(226, 238, 250)', maxHeight: '37px'}}
                    elevation={0}>
                    <Table sx={{width: '90%', flexGrow: 1}} size="small">
                        <TableHead>
                            <TableRow sx={{display: 'flex', justifyContent: 'space-between', p: 0.5}}>
                                <TableCell
                                    sx={{textAlign: 'center', ml: 1}}
                                    sortDirection={orderBy === 'name' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('name')}
                                    >
                                        {config.nameColumnLabel}
                                    </TableSortLabel>
                                </TableCell>
                                {config.showGroupSelector && <TableCell
                                    sx={{flexGrow: 1, textAlign: 'right'}}
                                    sortDirection={orderBy === 'group' ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'group'}
                                        direction={orderBy === 'group' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('group')}
                                    >
                                        Group
                                    </TableSortLabel>
                                </TableCell>}
                            </TableRow>
                        </TableHead>

                    </Table>
                </Paper>

                }

                {config.showTable && <ExplorerTreeView
                    exploreMode={exploreMode}
                    nodes={convertToTreeViewItems(objects)}
                    onClickItem={onClickItem}
                    expandedItems={expanded}
                    onExpandedItemsChange={(_event: React.SyntheticEvent, nodeIds: string[]): void => {
                        setExpanded(nodeIds);
                    }}
                    onSelectedItemsChange={handleTreeViewSelection}
                />}

                {config.useOccurrenceReportDescriptorTable && <OccurrenceReportDescriptorTable
                    exploreMode={exploreMode}
                    nodes={convertToTreeViewItems(objects)}
                    onClickItem={onClickItem}
                    onRequestSort={handleRequestSort}
                    order={order}
                    orderBy={orderBy}
                />}

                {config.useOccurrenceReportTable && <OccurrenceReportTable
                    exploreMode={exploreMode}
                    nodes={convertToTreeViewItems(objects)}
                    onClickItem={onClickItem}
                    onRequestSort={handleRequestSort}
                    order={order}
                    orderBy={orderBy}
                />}

                {config.useNewOccurrenceReportTable && <NewOccurrenceReportTable
                    exploreMode={exploreMode}
                    nodes={convertToTreeViewItems(objects)}
                    onClickItem={onClickItem}
                    onRequestSort={handleRequestSort}
                    order={order}
                    orderBy={orderBy}
                />}

            </React.Fragment>

            <ExplorerPagination
                page={page}
                objects={objects}
                pageSizes={PAGE_SIZES}
                uuidRef={uuidRef.current}
                onPageSizeChange={onPageSizeChange}
                onPageChange={onPageChange}
            />

        </Stack>
    </Box>)
}